// Third-party
import React from 'react';
import classNames from 'classnames';
import { withRouter, RouteComponentProps, Link } from 'react-router-dom';
import camelCase from 'lodash/camelCase';

// Components
import { PillLabel } from '../misc';
import { InfoTooltip } from '@/views/components/tooltips';

// Utils
import { PILL_COLORS } from '@/utils/colors';
import { formatMessage } from '@/utils/i18n';
import NavigationItem from '@/types/navigation-item';

export interface BaseNavigationProps {
  items: NavigationItem[];
  onChange?: (item: NavigationItem) => void;
  className?: string;
  selectedId?: string;
  replaceUrl?: boolean;
  dark?: boolean;
}

type Props = BaseNavigationProps & RouteComponentProps;

const renderNavItems = (items: NavigationItem[], path: string, onChange?: (item: NavigationItem) => void, selectedId?: string, replaceUrl?: boolean) =>
  items
    .filter(item => !item.hidden)
    .map((item, i) => {
      let selected;

      if (item.exact) {
        selected = path === item.value || !!(item.subValues && item.subValues.includes(path));
      } else {
        selected = (item.value && path.startsWith(item.value)) || (item.id && item.id === selectedId);
      }
      const classes = classNames('navigation-item', { selected, disabled: item.disabled });

      const onClick = (event: React.MouseEvent) => {
        event.stopPropagation();

        if (item.disabled) {
          return;
        }

        if (item.callback) {
          // Nav item defines a callback function that overrides normal navigation behaviour
          item.callback();
          return;
        }

        if (onChange) {
          onChange(item);
        }
      };

      const isDisabled = (event: React.MouseEvent) => {
        if (item.disabled || item.callback) {
          return event.preventDefault();
        }
      };

      return (
        <li className={classes} onClick={onClick} key={i}>
          <Link replace={replaceUrl} onClick={isDisabled} className="d-flex align-items-center" to={item.value || '#'} aria-label={item.label}>
            <span className="navigation-item-label">
              {item.icon && <i className={`icon ${item.icon}`} />}
              <span>{item.element || item.label}</span>
            </span>
            {item.tooltipMessage && <InfoTooltip className="ms-1" message={formatMessage(item.tooltipMessage)} />}
            {item.pill && <PillLabel color={PILL_COLORS.GREEN} text={item.pill} />}
          </Link>
        </li>
      );
    });

const BaseNavigation = ({ items = [], location, onChange = () => {}, className, selectedId, replaceUrl, dark }: Props) => (
  <nav className={classNames('navigation', { dark }, className)}>{renderNavItems(items, location.pathname, onChange, selectedId, replaceUrl)}</nav>
);

export default withRouter(BaseNavigation);
